import React, { useEffect, useState } from 'react';
import isEqual from 'react-fast-compare';
import { useDispatch } from 'react-redux';

import { FlexColumn, Gap } from 'ui-components/components/flex';

import { HelpfulLinks } from './helpful-links-section';
import { MobileConfigurationSection } from './mobile-configuration-section';
import { PrintedSection } from './printed-section';
import style from './style.module.less';
import { SellerSettings } from '../../../../enums/seller-settings/seller-settings';
import { useEnablePickPackSettingsMobileConfiguration } from '../../../../feature-flags/hooks/use-enable-pick-pack-settings-mobile-configuration';
import {
  useMobileDefaultToteQuantity,
  useMobileRequireLocationVerification,
  useMobileShowLocationStep,
  useMobileSortItemsBy,
  usePickListGrouping,
  usePickListSorting,
  usePickListImages,
  usePickListOrderNumbers,
  usePickListZeroQuantity,
  usePickListInventoryCounts,
  usePickListMasterSku,
  usePickListHideItemOptions,
  usePickListWarehouseLocation,
} from '../../../../hooks/use-seller-setting';
import { saveNewSellerSettings } from '../../../../store/seller/seller.actions';
import { booleanToSkuSourceString, skuSourceStringToBoolean } from '../../../../utils/sku-source-conversion';
import { Segment } from '../../../../services/analytics-services/segment';
import { PICKING_SETTINGS_SAVED, PICKING_SETTINGS_VIEWED } from '../../../../constants/analytics-events';
import { updateSellerSettingsBulk } from '../../api/save-settings';
import SaveBar from '../../components/save-bar';
import { SettingsPageTitle } from '../../components/settings-page-title/settings-page-title';
import messages from '../../messages';
import settingsPageStyle from '../../settings-page.module.less';

export const WarehousePicking = () => {
  const dispatch = useDispatch();
  const isMobileConfigurationEnabled = useEnablePickPackSettingsMobileConfiguration();

  useEffect(() => {
    Segment.trackEvent(PICKING_SETTINGS_VIEWED);
  }, []);

  const currentToteQuantity = useMobileDefaultToteQuantity();
  const currentShowLocationStep = useMobileShowLocationStep();
  const currentLocationVerification = useMobileRequireLocationVerification();
  const currentSortItemsBy = useMobileSortItemsBy();

  const currentGrouping = usePickListGrouping();
  const currentSorting = usePickListSorting();
  const currentImages = usePickListImages();
  const currentOrderNumbers = usePickListOrderNumbers();
  const currentZeroQuantity = usePickListZeroQuantity();
  const currentInventoryCounts = usePickListInventoryCounts();
  const currentMasterSku = usePickListMasterSku();
  const currentHideOptionSetting = usePickListHideItemOptions();
  const currentWarehouseLocation = usePickListWarehouseLocation();

  const originalMobileConfigData = {
    defaultToteQuantity: currentToteQuantity,
    showLocationStep: currentShowLocationStep,
    requireLocationVerification: currentLocationVerification,
    sortItemsBy: currentSortItemsBy,
  };

  const originalPrintedData = {
    groupBy: currentGrouping,
    sortBy: currentSorting,
    showImages: currentImages,
    showOrderNumbers: currentOrderNumbers,
    hideZeroQuantity: currentZeroQuantity,
    showInventoryCounts: currentInventoryCounts,
    masterSku: booleanToSkuSourceString(currentMasterSku),
    hideItemOptions: currentHideOptionSetting,
    PickListWarehouseLocation: currentWarehouseLocation,
  };

  const [mobileConfigFormData, setMobileConfigFormData] = useState(originalMobileConfigData);
  const [printedFormData, setPrintedFormData] = useState(originalPrintedData);

  const hasMobileConfigChanges = !isEqual(mobileConfigFormData, originalMobileConfigData) && isMobileConfigurationEnabled;
  const hasPrintedChanges = !isEqual(printedFormData, originalPrintedData);
  const hasAnyChanges = hasMobileConfigChanges || hasPrintedChanges;

  const handleMobileConfigFieldChange = (field, value) => {
    setMobileConfigFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePrintedFieldChange = (field, value) => {
    setPrintedFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    const settings = {};

    if (hasMobileConfigChanges) {
      settings[SellerSettings.mobileDefaultToteQuantity.key] = mobileConfigFormData.defaultToteQuantity;
      settings[SellerSettings.mobileShowLocationStep.key] = mobileConfigFormData.showLocationStep;
      settings[SellerSettings.mobileRequireLocationVerification.key] = mobileConfigFormData.requireLocationVerification;
      settings[SellerSettings.mobileSortItemsBy.key] = mobileConfigFormData.sortItemsBy;
    }

    if (hasPrintedChanges) {
      settings[SellerSettings.pickListGrouping.key] = printedFormData.groupBy;
      settings[SellerSettings.pickListSorting.key] = printedFormData.sortBy;
      settings[SellerSettings.pickListImages.key] = printedFormData.showImages;
      settings[SellerSettings.pickListOrderNumbers.key] = printedFormData.showOrderNumbers;
      settings[SellerSettings.pickListZeroQuantity.key] = printedFormData.hideZeroQuantity;
      settings[SellerSettings.pickListInventoryCounts.key] = printedFormData.showInventoryCounts;
      settings[SellerSettings.pickListMasterSku.key] = skuSourceStringToBoolean(printedFormData.masterSku);
      settings[SellerSettings.pickListHideItemOptions.key] = printedFormData.hideItemOptions;
      settings[SellerSettings.pickListWarehouseLocation.key] = printedFormData.PickListWarehouseLocation;
    }

    const newSettings = await updateSellerSettingsBulk(settings);
    dispatch(saveNewSellerSettings(newSettings));
    Segment.trackEvent(PICKING_SETTINGS_SAVED, {
      [SellerSettings.mobileDefaultToteQuantity.key]: mobileConfigFormData.defaultToteQuantity,
      [SellerSettings.mobileShowLocationStep.key]: mobileConfigFormData.showLocationStep,
      [SellerSettings.mobileRequireLocationVerification.key]: mobileConfigFormData.requireLocationVerification,
      [SellerSettings.mobileSortItemsBy.key]: mobileConfigFormData.sortItemsBy,
      [SellerSettings.pickListGrouping.key]: printedFormData.groupBy,
      [SellerSettings.pickListSorting.key]: printedFormData.sortBy,
      [SellerSettings.pickListImages.key]: printedFormData.showImages,
      [SellerSettings.pickListOrderNumbers.key]: printedFormData.showOrderNumbers,
      [SellerSettings.pickListZeroQuantity.key]: printedFormData.hideZeroQuantity,
      [SellerSettings.pickListInventoryCounts.key]: printedFormData.showInventoryCounts,
      [SellerSettings.pickListMasterSku.key]: skuSourceStringToBoolean(printedFormData.masterSku),
      [SellerSettings.pickListHideItemOptions.key]: printedFormData.hideItemOptions,
      [SellerSettings.pickListWarehouseLocation.key]: printedFormData.PickListWarehouseLocation,
    });
  };

  return (
    <>
      <div className={settingsPageStyle.content}>
        <SettingsPageTitle value={messages.warehousePicking} />
        <div className={style.elements}>
          <FlexColumn className={style.elementsLeft} gap={Gap.Space24}>
            {isMobileConfigurationEnabled ? (
              <MobileConfigurationSection onSettingsChange={handleMobileConfigFieldChange} values={mobileConfigFormData} />
            ) : null}
            <PrintedSection onChange={handlePrintedFieldChange} values={printedFormData} />
          </FlexColumn>
          <div className={style.elementsRight}>
            <HelpfulLinks />
          </div>
        </div>
      </div>
      <SaveBar className={style['footer-container']} disabled={!hasAnyChanges} onSave={handleSave} />
    </>
  );
};
